/**
 * The route table (spec M0-F4 §7.1).
 *
 * The paths are monday's, verbatim, including the legacy `pulses` and `my_week` segments
 * `[cap]` — every link in a migrated update, e-mail or bookmark keeps working after the base
 * URL is swapped, and `Board.url` / `Item.url` / `Item.relative_link` are built from the same
 * table (`modules/shell/urls.ts`, §8).
 *
 * Every route element sits behind its own error boundary, so a throw inside one screen never
 * unmounts the shell (N2).
 */
import { createBrowserRouter, type RouteObject } from 'react-router';
import { AppShell } from '../app/AppShell';
import {
  ArchiveRoute,
  BoardRedirect,
  BoardView,
  HomeRoute,
  InboxRoute,
  MyWeekRedirect,
  MyWorkRoute,
  NotificationSettingsRoute,
  TasksRoute,
  NotAvailableYet,
  NotFound,
  RouteErrorBoundary,
  TrashRoute,
  WorkspaceHome,
} from './screens';
import { AcceptInvitation } from './auth/AcceptInvitation';
import { ForgotPassword, ResetPassword } from './auth/ForgotPassword';
import { Login } from './auth/Login';
import { Logout } from './auth/Logout';
import { SsoError } from './auth/SsoError';
import { AuthHarness } from './auth/harness';
import { VerifyDevice } from './auth/VerifyDevice';
import { Developers } from './profile/Developers';
import { AdminRoute, ProfileRoute } from '../modules/admin/routes';
import { BoardHarness } from './ui/BoardHarness';
import { NotificationsHarness } from '../modules/notifications/harness';
import { CollabHarness } from '../modules/my-work/harness';
import { TasksHarness } from '../modules/tasks/harness';
import { FormsHarness } from '../modules/forms/harness';
import { AutomationsHarness } from '../modules/automations/harness';
import { AdminHarness } from '../modules/admin/harness';
import { DashboardHarness } from '../modules/dashboards/harness';
import { ViewsHarness } from '../modules/views/harness';
import { TimelineHarness } from '../modules/views/gantt/harness';
import { DashboardPage } from '../modules/dashboards/DashboardPage';
import { DemoShell } from './ui/DemoShell';
import { UiGallery } from './ui/UiGallery';
import { PublicFormPage } from '../modules/forms/PublicFormPage';
import { DocListPage, DocPage } from '../modules/docs';
import { DocsHarness } from '../modules/docs/harness';
/* M8-P4: the Developers section's two new pages. Lazy would buy nothing — the heavy half is
   GraphiQL, which `PlaygroundPage` already loads on demand. */
import { ApiDocsPage, GuidePage, PlaygroundPage } from '../modules/developers';
import { DevelopersHarness } from '../modules/developers/harness';

export const routes: RouteObject[] = [
  /**
   * The auth screens (M0-F3 §8) live **outside** the shell: they have their own 64 px header
   * and 376 px column, and they must render before there is a session to build a sidebar from.
   */
  { path: '/login', element: <Login />, errorElement: <RouteErrorBoundary /> },
  { path: '/login/verify', element: <VerifyDevice />, errorElement: <RouteErrorBoundary /> },
  /**
   * P-038. monday's own logout URL, and a real route rather than the shell's `*` NotFound.
   * Outside the shell on purpose: it must not run a single bootstrap query for the session it
   * is revoking (M0-F3 §5.4).
   */
  { path: '/logout', element: <Logout />, errorElement: <RouteErrorBoundary /> },
  /**
   * M0-F3b §7: the platform sign-in refusal pages. One screen, one closed set of reasons, on
   * the same auth shell the person just came from.
   */
  { path: '/login/sso-error', element: <SsoError />, errorElement: <RouteErrorBoundary /> },
  { path: '/forgot-password', element: <ForgotPassword />, errorElement: <RouteErrorBoundary /> },
  { path: '/reset-password/:token', element: <ResetPassword />, errorElement: <RouteErrorBoundary /> },
  { path: '/invite/:token', element: <AcceptInvitation />, errorElement: <RouteErrorBoundary /> },

  /**
   * M4.V8: the public form. **Outside the shell**, like the auth screens: it renders for a
   * visitor with no session, no sidebar and no top bar, and the api inlines its payload into
   * the shell it serves so the first paint needs no round trip (`specs/M4-V8-forms.md` §6).
   */
  { path: '/forms/:token', element: <PublicFormPage />, errorElement: <RouteErrorBoundary /> },

  /**
   * The visual-regression harness (M0-F7 §9 U9 / §10, M1-B4). It lives **outside** the shell on
   * purpose: it must render with no session, no api and no database, which is what lets the
   * table view be screenshotted and diffed against the reference captures in CI.
   */
  { path: '/__ui', element: <UiGallery />, errorElement: <RouteErrorBoundary /> },
  /**
   * Parity card P-002: a *screen* fixture renders inside the real shell. `DemoShell` is the
   * product's own `<AppShell/>` with the three shell queries answered from fixture data
   * (`modules/shell/demo.tsx`), so the harness still needs no session, no api and no database.
   */
  {
    path: '/__ui/board',
    element: <DemoShell />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <BoardHarness />, errorElement: <RouteErrorBoundary /> }],
  },

  /** M0-F3b's login button and refusal pages, fixture-backed (`?screen=login|sso-*`). */
  { path: '/__ui/auth', element: <AuthHarness />, errorElement: <RouteErrorBoundary /> },

  /** M6-A2's centre, fixture-backed, for the same reason (`?screen=create|manage|runs`). */
  {
    path: '/__ui/automations',
    element: <AutomationsHarness />,
    errorElement: <RouteErrorBoundary />,
  },

  /** M4.V8's surfaces, fixture-backed (`?screen=public|validation|thankyou|builder`). */
  { path: '/__ui/forms', element: <FormsHarness />, errorElement: <RouteErrorBoundary /> },

  /**
   * M8's admin, permission and profile surfaces, fixture-backed, for the same reason
   * (`?screen=users|invitations|teams|general|permissions|security|tokens|audit|usage|
   * board-permissions|share|share-public|invite|invite-member|profile|workspace-members`).
   */
  { path: '/__ui/admin', element: <AdminHarness />, errorElement: <RouteErrorBoundary /> },

  /** M7's widget canvas, fixture-backed, for the same reason (`?screen=canvas|empty|readonly`). */
  { path: '/__ui/dashboard', element: <DashboardHarness />, errorElement: <RouteErrorBoundary /> },

  /** M4 V2/V4/V7's three renderers, fixture-backed (`?screen=kanban|calendar|gallery`). */
  { path: '/__ui/views', element: <ViewsHarness />, errorElement: <RouteErrorBoundary /> },
  /** M4.V3/V5/V6 — the gantt, timeline and workload renderers on the fixture source. */
  { path: '/__ui/timeline', element: <TimelineHarness />, errorElement: <RouteErrorBoundary /> },

  /** Round 5m's three surfaces, fixture-backed (`?screen=my-work|search|subscribers`). */
  { path: '/__ui/collab', element: <CollabHarness />, errorElement: <RouteErrorBoundary /> },

  /** M10's Tasks page, fixture-backed (`?screen=mine|assigned|archive|empty`). */
  { path: '/__ui/tasks', element: <TasksHarness />, errorElement: <RouteErrorBoundary /> },
  /**
   * P-319 · the same harness with the item card open over it, so the "card over the Tasks page"
   * contract is hit-testable with no session. Same element as the line above, which is what keeps
   * `TasksHarness` — and therefore the list, its filters, its selection and its scroll — mounted
   * across the two URLs.
   */
  { path: '/__ui/tasks/pulses/:itemId', element: <TasksHarness />, errorElement: <RouteErrorBoundary /> },

  /** M11's doc renderer on a fixture built from the account's real block shapes. */
  { path: '/__ui/docs', element: <DocsHarness />, errorElement: <RouteErrorBoundary /> },
  /** M8-P4 §9: the API documentation and the playground chrome, on fixtures. */
  { path: '/__ui/developers', element: <DevelopersHarness />, errorElement: <RouteErrorBoundary /> },

  /** M5's three surfaces, fixture-backed, for the same reason (`?screen=bell|settings|inbox`). */
  {
    path: '/__ui/notifications',
    element: <NotificationsHarness />,
    errorElement: <RouteErrorBoundary />,
  },

  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomeRoute />, errorElement: <RouteErrorBoundary /> },

      { path: 'workspaces', element: <WorkspaceHome />, errorElement: <RouteErrorBoundary /> },
      /** M7 D1: a standalone dashboard. This account has none; monday's API creates them. */
      { path: 'dashboards/:dashboardId', element: <DashboardPage />, errorElement: <RouteErrorBoundary /> },
      { path: 'workspaces/:workspaceId', element: <WorkspaceHome />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'workspaces/:workspaceId/content-management',
        element: <WorkspaceHome />,
        errorElement: <RouteErrorBoundary />,
      },

      { path: 'boards/:boardId', element: <BoardRedirect />, errorElement: <RouteErrorBoundary /> },
      // Observed deep link: an item URL with no view context resolves the view first.
      {
        path: 'boards/:boardId/pulses/:itemId',
        element: <BoardRedirect />,
        errorElement: <RouteErrorBoundary />,
      },
      /**
       * P-325: the Update feed's group link (`/boards/<b>/groups/<g>`, monday's own shape) lands
       * on the board, as monday's does `[cap:notifications/11o-click-group]`.
       */
      { path: 'boards/:boardId/groups/:groupId', element: <BoardRedirect />, errorElement: <RouteErrorBoundary /> },
      { path: 'boards/:boardId/views/:viewId', element: <BoardView />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'boards/:boardId/views/:viewId/pulses/:itemId',
        element: <BoardView />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'boards/:boardId/views/:viewId/activity_log',
        element: <BoardView />,
        errorElement: <RouteErrorBoundary />,
      },

      /**
       * M5.C3. monday's own `/my_week` 404s in the 2026 layout and My Work is `/my_work`;
       * ours keeps the legacy path alive as a redirect so migrated links still resolve
       * (`specs/M7-C3-inbox-my-work.md` D-1).
       */
      { path: 'my_week', element: <MyWeekRedirect />, errorElement: <RouteErrorBoundary /> },
      { path: 'my_work', element: <MyWorkRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'my_inbox', element: <InboxRoute />, errorElement: <RouteErrorBoundary /> },

      /**
       * M10 · the Tasks section (`docs/13-tasks-section-proposal.md`). **Not monday** — monday
       * has no such section and no such URL — so the path is the product's own, and the tab is
       * a path segment rather than a query parameter so that every tab is a link somebody can
       * send. The two panel routes resolve even before round T3's panels ship: the page mounts
       * them lazily and `Suspense` covers the gap.
       */
      { path: 'tasks', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'tasks/assigned', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'tasks/archive', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'tasks/insights', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'tasks/timeline', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      /**
       * **P-319** · the item card opens **over** the Tasks page, never by leaving it (Roberto,
       * 2026-10-06: *"quando si clicca su una task, di rimanere sulla pagina task"*). The card is a
       * URL segment exactly as it is on a board (R3) — `pulses/:itemId`, monday's own legacy
       * segment — so the card is deep-linkable and Back closes it.
       *
       * One such route **per tab**, and each is a sibling of its tab's own route rendering the same
       * `<TasksRoute />`: the tab is part of the list state the card must not destroy, so a card
       * opened from Tasks Assigned closes back onto Tasks Assigned. React reconciles the two
       * matches as one element, which is what keeps the page — and its scroll and selection —
       * mounted underneath the panel.
       */
      { path: 'tasks/pulses/:itemId', element: <TasksRoute />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'tasks/assigned/pulses/:itemId',
        element: <TasksRoute />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'tasks/archive/pulses/:itemId',
        element: <TasksRoute />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'tasks/insights/pulses/:itemId',
        element: <TasksRoute />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'tasks/timeline/pulses/:itemId',
        element: <TasksRoute />,
        errorElement: <RouteErrorBoundary />,
      },

      /**
       * M11. The segment is monday's `object_id` — the id its own `/docs/<n>` links carry —
       * so every migrated doc link keeps resolving (doc 00). The api accepts either id.
       */
      { path: 'docs', element: <DocListPage />, errorElement: <RouteErrorBoundary /> },
      { path: 'docs/:docId', element: <DocPage />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'dashboards/:dashboardId',
        element: <NotAvailableYet what="Dashboards" module="M7" />,
        errorElement: <RouteErrorBoundary />,
      },
      /** M8.P2 §6.1. A non-admin gets F4's 403 panel, never an empty console. */
      { path: 'admin', element: <AdminRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'admin/*', element: <AdminRoute />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'me/notifications',
        element: <NotificationSettingsRoute />,
        errorElement: <RouteErrorBoundary />,
      },
      { path: 'me/profile', element: <ProfileRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'me/developers', element: <Developers />, errorElement: <RouteErrorBoundary /> },
      /*
       * M8-P4. They sit at `/developers/*` rather than under `/me/`, because the API reference
       * is a fact about the deployment, not about the person reading it; `/me/developers` stays
       * the personal token page it has always been, and the Developer Center's left nav links
       * all four together.
       */
      { path: 'developers/docs', element: <ApiDocsPage />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'developers/docs/getting-started',
        element: <GuidePage guideId="getting-started" />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'developers/docs/migrating',
        element: <GuidePage guideId="migrating" />,
        errorElement: <RouteErrorBoundary />,
      },
      { path: 'developers/playground', element: <PlaygroundPage />, errorElement: <RouteErrorBoundary /> },
      { path: 'trash', element: <TrashRoute />, errorElement: <RouteErrorBoundary /> },
      { path: 'archive', element: <ArchiveRoute />, errorElement: <RouteErrorBoundary /> },
      {
        path: 'help',
        element: <NotAvailableYet what="Help" module="M8.P2" />,
        errorElement: <RouteErrorBoundary />,
      },

      { path: '*', element: <NotFound />, errorElement: <RouteErrorBoundary /> },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
